import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import type {
  PropertyChild,
  PropertyChildInput,
} from "../../types/propertyChildren";
import { TOWER_BLOCK_MAX_LENGTH } from "../../types/propertyChildren";
import {
  activateProperty,
  addPropertyChildren,
  fetchPropertyChildren,
  removePropertyChild,
} from "../../services/propertyChildren";
import {
  buildUnitReference,
  findDuplicateChildIndex,
  validateChildInput,
} from "../../utils/propertyChildren";
import ModalDeactivateProperty from "../ModalDeactivateProperty";
import ModalConfirmActivateProperty from "../ModalConfirmActivateProperty";
import ModalConfirmRemoveProperty from "../ModalConfirmRemoveProperty";
import { FLOW_TEXT_INPUT_BASE } from "../../styles/twDesignPrimitives";

type RentReason = { id: number; reason: string };

type CommonProps = {
  /** Notifies parent so complement/floor can be disabled while units exist. */
  onChildrenCountChange?: (count: number) => void;
};

type Props =
  | (CommonProps & {
      mode: "local";
      value: PropertyChildInput[];
      onChange: (next: PropertyChildInput[]) => void;
    })
  | (CommonProps & {
      mode: "manage";
      propertyId: number;
      rentReasons?: RentReason[];
      /** When false, hides rented/activate actions (useful during create draft). */
      allowStatusActions?: boolean;
    });

const EMPTY: PropertyChildInput = {
  floor: "",
  apartment_number: "",
  tower_block: "",
};

const disabledInputClass = `${FLOW_TEXT_INPUT_BASE} appearance-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500`;

export default function PropertyChildrenSection(props: Props) {
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();

  const isManage = props.mode === "manage";
  const propertyId = props.mode === "manage" ? props.propertyId : null;
  const allowStatusActions =
    props.mode === "manage" ? props.allowStatusActions !== false : false;
  const localValue = props.mode === "local" ? props.value : [];
  const onLocalChange = props.mode === "local" ? props.onChange : undefined;
  const incomingRentReasons =
    props.mode === "manage" ? props.rentReasons : undefined;
  const onChildrenCountChangeRef = useRef(props.onChildrenCountChange);
  onChildrenCountChangeRef.current = props.onChildrenCountChange;

  const [children, setChildren] = useState<PropertyChild[]>([]);
  const [draft, setDraft] = useState<PropertyChildInput>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [childToDeactivate, setChildToDeactivate] = useState<number | null>(
    null
  );
  const [childToActivate, setChildToActivate] = useState<number | null>(null);
  const [activating, setActivating] = useState(false);
  const [rentReasons, setRentReasons] = useState<RentReason[]>([]);
  const [childPendingRemoval, setChildPendingRemoval] =
    useState<PropertyChild | null>(null);
  const [localIndexPendingRemoval, setLocalIndexPendingRemoval] = useState<
    number | null
  >(null);

  const notifyChildrenCount = useCallback((count: number) => {
    onChildrenCountChangeRef.current?.(count);
  }, []);

  const authorize = useCallback(async () => {
    const token = await refreshToken(dateToExpires, access_token);
    api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
  }, [access_token, dateToExpires]);

  const loadChildren = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      await authorize();
      const list = await fetchPropertyChildren(api, propertyId);
      setChildren(list);
      notifyChildrenCount(list.length);
    } catch (err: any) {
      addToast({
        title:
          err?.response?.data?.message ?? "Erro ao carregar unidades filhas",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, authorize, notifyChildrenCount, propertyId]);

  useEffect(() => {
    if (isManage && propertyId) {
      loadChildren();
      return;
    }
    notifyChildrenCount(localValue.length);
  }, [isManage, propertyId, loadChildren, localValue.length, notifyChildrenCount]);

  useEffect(() => {
    if (incomingRentReasons?.length) setRentReasons(incomingRentReasons);
  }, [incomingRentReasons]);

  const ensureRentReasons = useCallback(async () => {
    if (rentReasons.length > 0 || !propertyId) return;
    await authorize();
    const response = await api.get(`/api/adm/properties/${propertyId}`);
    setRentReasons(response.data?.data?.rentReasons ?? []);
  }, [authorize, propertyId, rentReasons.length]);

  async function handleAdd() {
    const fieldError = validateChildInput(draft);
    if (fieldError) {
      addToast({ title: fieldError, type: "error" });
      return;
    }

    const existing: PropertyChildInput[] = isManage
      ? children.map((c) => ({
          floor: c.floor,
          apartment_number: c.apartment_number,
          tower_block: c.tower_block,
        }))
      : localValue;

    if (findDuplicateChildIndex(existing, draft) >= 0) {
      addToast({
        title:
          "Já existe uma unidade com a mesma torre, andar e apartamento.",
        type: "error",
      });
      return;
    }

    if (!isManage) {
      const next = [
        ...localValue,
        {
          floor: draft.floor.trim(),
          apartment_number: draft.apartment_number.trim(),
          tower_block: draft.tower_block?.trim() ?? "",
        },
      ];
      onLocalChange?.(next);
      notifyChildrenCount(next.length);
      setDraft(EMPTY);
      return;
    }
    if (!propertyId) return;

    setSaving(true);
    try {
      await authorize();
      await addPropertyChildren(api, propertyId, [draft]);
      setDraft(EMPTY);
      addToast({ title: "Unidade filha adicionada.", type: "success" });
      await loadChildren();
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao adicionar unidade",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveChild(child: PropertyChild) {
    if (!propertyId || child.is_rented) return;
    setSaving(true);
    try {
      await authorize();
      await removePropertyChild(api, propertyId, child.id);
      addToast({ title: "Unidade removida.", type: "success" });
      setChildPendingRemoval(null);
      await loadChildren();
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao remover unidade",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  function handleConfirmRemove() {
    if (isManage && childPendingRemoval) {
      handleRemoveChild(childPendingRemoval);
      return;
    }

    if (localIndexPendingRemoval != null) {
      const next = localValue.filter((_, i) => i !== localIndexPendingRemoval);
      onLocalChange?.(next);
      notifyChildrenCount(next.length);
      setLocalIndexPendingRemoval(null);
    }
  }

  function closeRemoveConfirmModal() {
    setChildPendingRemoval(null);
    setLocalIndexPendingRemoval(null);
  }

  async function handleActivateChild() {
    if (childToActivate == null) return;
    setActivating(true);
    try {
      await authorize();
      await activateProperty(api, childToActivate);
      addToast({ title: "Unidade reativada.", type: "success" });
      setChildToActivate(null);
      await loadChildren();
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao reativar unidade",
        type: "error",
      });
    } finally {
      setActivating(false);
    }
  }

  const rows: Array<{
    key: string | number;
    label: string;
    isRented?: boolean;
    child?: PropertyChild;
    localIndex?: number;
  }> = isManage
    ? children.map((child) => ({
        key: child.id,
        label: child.unit_reference,
        isRented: child.is_rented,
        child,
      }))
    : localValue.map((item, index) => ({
        key: `${item.tower_block ?? ""}-${item.floor}-${item.apartment_number}-${index}`,
        label: buildUnitReference(
          item.floor,
          item.apartment_number,
          item.tower_block
        ),
        localIndex: index,
      }));

  const canAddChild =
    !saving &&
    draft.floor.trim().length > 0 &&
    draft.apartment_number.trim().length > 0;

  return (
    <section className="mt-8 w-full rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-800">Anúncios filhos</h3>
        <p className="mt-1 text-xs text-slate-500">
          Cada unidade usa o endereço do pai com torre/bloco e andar próprios.
          Referência: &quot;Torre/Bloco X - Andar Y - Apto Z&quot;.
        </p>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Torre/Bloco (opcional)
          </label>
          <input
            value={draft.tower_block ?? ""}
            maxLength={TOWER_BLOCK_MAX_LENGTH}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, tower_block: e.target.value }))
            }
            className={disabledInputClass}
            placeholder="Ex.: A"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Andar
          </label>
          <input
            value={draft.floor}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, floor: e.target.value }))
            }
            className={disabledInputClass}
            placeholder="Ex.: 3"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Apartamento
          </label>
          <input
            value={draft.apartment_number}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                apartment_number: e.target.value,
              }))
            }
            className={disabledInputClass}
            placeholder="Ex.: 301"
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            disabled={!canAddChild}
            aria-disabled={!canAddChild}
            onClick={handleAdd}
            className={`inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white sm:w-auto ${
              canAddChild
                ? "cursor-pointer bg-[#6EA5FF] hover:bg-[#5c94ed]"
                : "cursor-not-allowed bg-[#6EA5FF] opacity-40"
            }`}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Adicionar
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Carregando unidades…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">
          Nenhuma unidade filha cadastrada.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li
              key={row.key}
              className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="text-sm font-medium text-slate-800">{row.label}</p>
                {row.isRented != null && (
                  <p className="text-xs text-slate-500">
                    {row.isRented ? "Alugado" : "Disponível"}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {isManage && allowStatusActions && row.child && (
                  <>
                    {!row.child.is_rented ? (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await ensureRentReasons();
                            setChildToDeactivate(row.child!.id);
                          } catch (err: any) {
                            addToast({
                              title:
                                err?.response?.data?.message ??
                                "Erro ao preparar desativação",
                              type: "error",
                            });
                          }
                        }}
                        className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Marcar como alugado
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setChildToActivate(row.child!.id)}
                        className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                      >
                        Reativar
                      </button>
                    )}
                  </>
                )}
                {(!isManage || (row.child && !row.child.is_rented)) && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      if (isManage && row.child) {
                        setChildPendingRemoval(row.child);
                        return;
                      }
                      setLocalIndexPendingRemoval(row.localIndex ?? null);
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Remover
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {childToDeactivate != null && (
        <ModalDeactivateProperty
          open
          property={childToDeactivate}
          rentReasons={rentReasons}
          functionToCloseModal={() => setChildToDeactivate(null)}
          onSuccessDeactivate={() => {
            setChildToDeactivate(null);
            loadChildren();
          }}
        />
      )}

      {childToActivate != null && (
        <ModalConfirmActivateProperty
          open
          isLoading={activating}
          functionToActivateProperty={handleActivateChild}
          functionToCloseModal={() => setChildToActivate(null)}
        />
      )}

      <ModalConfirmRemoveProperty
        open={childPendingRemoval != null || localIndexPendingRemoval != null}
        isLoading={saving}
        message={
          childPendingRemoval
            ? `Tem certeza que deseja excluir a unidade "${childPendingRemoval.unit_reference}"?`
            : "Tem certeza que deseja remover esta unidade filha?"
        }
        functionToRemoveProperty={handleConfirmRemove}
        functionToCloseModal={closeRemoveConfirmModal}
      />
    </section>
  );
}
